import { useEffect } from 'react';
import {
  Form,
  Button,
  TextInput,
  Typography,
  SelectInput,
  UploadInput,
  ButtonsWrapper,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { useForm } from 'antd/lib/form/Form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';
import { useGetDocumentLabelsByCompany } from 'src/hooks/documents-labels/get-documents-labels.hook';
import { useUpdateDocumentTemplate } from 'src/hooks/documents-templates/update-document-template.hook';
import { useGetDocumentTemplateById } from 'src/hooks/documents-templates/get-document-template-by-id.hook';

import { EditDocumentWrapper } from './edit-document-template.styles';

const EditDocumentTemplate = ({ documentTemplateId }: { documentTemplateId: string }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);
  const currentCompanyId = useGetCurrentCompanyId();
  const [form] = useForm();
  const { documentsLabels } = useGetDocumentLabelsByCompany(currentCompanyId);
  const { documentTemplate, loading } = useGetDocumentTemplateById(documentTemplateId);
  const { _id: fileId, filename: name, key, location } = documentTemplate?.file || {};

  const onCancel = () => navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.documents}`);
  const updateDocumentTemplate = useUpdateDocumentTemplate({ onSuccess: onCancel });

  useEffect(() => form.setFieldsValue(documentTemplate), [documentTemplate, form]);

  return (
    <EditDocumentWrapper>
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {t('settings:documents.labels.documentTemplate')}
      </Typography>
      <Form
        form={form}
        layout="vertical"
        onFinish={({ file, ...restDoc }) => {
          const { fileList = [] } = file || {};
          const payload = {
            _id: documentTemplateId,
            ...restDoc,
            ...(fileList.length && { file: fileList[0].originFileObj }),
          };
          updateDocumentTemplate({ variables: { payload } });
        }}
      >
        <Form.Item
          name="title"
          label={t('settings:permissionGroupSettings.labels.title')}
          rules={[
            { required: true, message: t('settings:permissionGroupSettings.errors.titleRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput maxLength={50} placeholder={t('settings:documents.labels.headline')} />
        </Form.Item>
        <Form.Item
          name="documentLabelId"
          label={t('common:labels.label')}
          rules={[
            { required: true, message: t('common:validation.error.labelRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <SelectInput placeholder={t('settings:documents.labels.selectLabel')}>
            {documentsLabels.map(({ _id: documentLabelId, name }: any) => (
              <SelectInput.Option key={documentLabelId} value={documentLabelId}>
                {name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="file"
          rules={[
            {
              required: true,
              message: t('settings:permissionGroupSettings.errors.fileRequired'),
            },
            () => ({
              validator(_, { fileList } = {}) {
                if (fileList) {
                  const [file] = fileList;

                  if (!file) {
                    return Promise.reject(
                      new Error(t('settings:permissionGroupSettings.errors.fileRequired'))
                    );
                  }

                  const extension = file.name.split('.').pop();
                  if (!/doc*/.test(extension)) {
                    return Promise.reject(
                      new Error(t('settings:documents.errors.wrongFileFormat'))
                    );
                  }

                  if (file.size > 1000 * 1000 * 20) {
                    return Promise.reject(
                      new Error(t('settings:documents.errors.wrongFileSize', { detail: 20 }))
                    );
                  }
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          {fileId && (
            <UploadInput
              variant="button"
              accept="doc*"
              btnText={t('settings:documents.buttons.attachFile')}
              showUploadList={!loading}
              maxCount={1}
              beforeUpload={() => false}
              defaultFileList={[{ uid: fileId, name: name || key, url: location }]}
            />
          )}
        </Form.Item>

        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </EditDocumentWrapper>
  );
};

export default EditDocumentTemplate;
